'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import classes from './NeumoVideo.module.scss';
import { IconPlayerPlay, IconX } from '@tabler/icons-react';
import { createPortal } from 'react-dom';

type NeumoVideoProps = {
  title: string;
  subtitle?: string;
  description?: string;
  videoUrl: string;
  posterUrl?: string;
  videoAlt: string;
  badge?: string;
  ctaLabel?: string;
  ctaHref?: string;
  id?: string;
};

const renderHighlightedTitle = (title: string) => {
  const lines = title.split('\n');
  return lines.map((line, lineIndex) => {
    const parts = line.split('*');
    const children = parts.map((part, i) => {
      if (i % 2 === 1) {
        return <em key={i}>{part}</em>;
      }
      const segments = part.split('—');
      return segments.map((segment, j) => (
        <Fragment key={j}>
          {segment}
          {j < segments.length - 1 && <span className={classes.titleDash}>—</span>}
        </Fragment>
      ));
    });
    return (
      <Fragment key={lineIndex}>
        {lineIndex > 0 && <br />}
        {children}
      </Fragment>
    );
  });
};

export const NeumoVideo = ({ data }: { data: NeumoVideoProps }) => {
  const {
    title,
    subtitle,
    description,
    videoUrl,
    posterUrl,
    videoAlt,
    badge,
    ctaLabel,
    ctaHref,
    id,
  } = data;
  const [isOpen, setIsOpen] = useState(false);
  const [canPortal, setCanPortal] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => setCanPortal(true), []);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  // ESC + lock scroll
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const modal = isOpen ? (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={classes.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={videoAlt}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          close();
        }
      }}
    >
      <div className={classes.modal}>
        <button
          className={classes.modalClose}
          onClick={close}
          aria-label="Fermer la vidéo"
          type="button"
        >
          <IconX size={20} />
        </button>

        <div className={classes.modalVideoContainer}>
          <video
            ref={videoRef}
            className={classes.modalVideo}
            controls
            autoPlay
            playsInline
            preload="auto"
          >
            <source src={videoUrl} type="video/mp4" />
            <track kind="captions" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className={classes.section} aria-labelledby="video-title">
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && <p className={classes.eyebrow}>{badge}</p>}
          <h2 id={id} className={classes.title}>
            {renderHighlightedTitle(title)}
          </h2>
          {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
        </div>

        <div className={classes.videoWrapper}>
          <div className={classes.textColumn}>
            {description && <p className={classes.leadDescription}>{description}</p>}

            {ctaLabel && ctaHref && (
              <div className={classes.ctaWrapper}>
                <a href={ctaHref} className={classes.ctaButton}>
                  {ctaLabel}
                  <span aria-hidden="true">→</span>
                </a>
                <p className={classes.ctaHint}>NDA signé sous 24h. Analyse sans engagement.</p>
              </div>
            )}
          </div>

          <div className={classes.videoColumn}>
            <div
              className={classes.videoContainer}
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
              aria-label="Ouvrir la vidéo"
            >
              {posterUrl ? (
                <Image
                  src={posterUrl}
                  alt={videoAlt}
                  fill
                  sizes="(max-width: 64em) 100vw, 50vw"
                  className={classes.posterNextImage}
                  priority={false}
                />
              ) : (
                <div className={classes.fallbackPoster} aria-hidden="true" />
              )}

              <div className={classes.posterOverlay}>
                <button
                  type="button"
                  className={classes.playButton}
                  onClick={open}
                  aria-label="Lire la vidéo"
                >
                  <IconPlayerPlay size={48} stroke={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Portal modal */}
      {canPortal && modal ? createPortal(modal, document.body) : null}
    </section>
  );
};
